import React, { useEffect, useState } from 'react';
import {
  PopUpGenerateWorksheet,
  PopUpFailedGenerateWorksheet,
  Snackbar,
  LoadingFull,
} from 'geniebook-ui/dist';
import { useNavigate, useParams } from 'react-router-dom';
import { useGenerateClass2SmartWorksheet } from '@hooks/student.hook';
import { IWorksheetCardProp } from '@models/interfaces/worksheet.interface';
import { subjectIdFromName } from '@utils/helpers/SubjectParser';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { BASE_URL, CDN_URL } from '@utils/constants/url.constants';
import { useIsJCUser } from '@hooks/user.hook';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

export type Class2SmartPopupHandlerProps = {
  data: IWorksheetCardProp;
  show: boolean;
  onClose: () => void;
};

const Class2SmartPopupHandler = ({
  data,
  show,
  onClose,
}: Class2SmartPopupHandlerProps) => {
  const { subjectId } = useParams();
  const { isJC } = useIsJCUser({
    subjectId: Number(subjectIdFromName(subjectId)),
  });
  const [snackbarProps, setSnackbarProps] = useState<{
    show: boolean;
    message: string;
  }>({
    show: false,
    message: '',
  });
  const [popupState, setPopupState] = useState<'idle' | 'generate' | 'failed'>(
    'idle',
  );
  const navigate = useNavigate();
  const generateClass2SmartWorksheetMutation =
    useGenerateClass2SmartWorksheet();

  useEffect(() => {
    if (show && popupState == 'idle') setPopupState('generate');
  }, [show]);
  const viewWidth = useViewWidth();

  const handleClose = () => {
    onClose();
    setPopupState('idle');
  };

  const showSnackbar = (message: string) => {
    setSnackbarProps({
      show: true,
      message: message,
    });
  };

  const generateWorksheet = () => {
    setPopupState('idle');

    // calling generate API
    generateClass2SmartWorksheetMutation.mutate(
      {
        generate_ws_mode: data.generate_ws_mode || '1',
        lesson_plan_topics: data.lesson_plan_topics
          .map(topic => topic.id)
          .join(','),
        subject_id: subjectIdFromName(subjectId),
        worksheet_name: data.worksheet_name,
        lesson_plan_knowledges: data.lesson_plan_knowledges
          .map(topic => topic.id)
          .join(','),
        online_lesson_id: data.online_lesson_id,
        time_limit: data.time_limit,
      },
      {
        onSuccess: res => {
          // check error
          if (res.data.error) {
            if (res.data?.data?.generate_code === 2) {
              // not enough question to generated
              setPopupState('failed');
            } else {
              // show snackbar, something is wrong
              showSnackbar(res.data.error_msg);
            }
          } else {
            if (res.data?.data?.generate_code === 2) {
              // not enough question to generated
              setPopupState('failed');
            } else {
              // success, open new worksheet + refresh current page
              window.open(
                `${BASE_URL}${subjectId}/worksheets/worksheet_question/${res.data?.data?.worksheet_id}`,
                '_blank',
              );
              window.location.reload();
            }
          }
        },
        onError: () => {
          // show snackbar
          showSnackbar('There is something wrong. Please try again.');
        },
      },
    );
    // if success, open new tab the generated worksheet + refresh current page
    // if failed, change status to failed
  };

  return (
    <>
      <PopUpGenerateWorksheet
        online_lesson_name={data.topic}
        show={popupState == 'generate'}
        onClose={handleClose}
        onCreateWorksheet={generateWorksheet}
        className="!max-w-[calc(100%-32px)]"
      />
      <PopUpFailedGenerateWorksheet
        subtopics={data.lesson_plan_knowledges}
        show={popupState == 'failed'}
        onClose={handleClose}
        onConfirm={() => {
          navigate(`/${subjectId}/worksheets/create`);
          handleClose();
        }}
        className="!max-w-[calc(100%-32px)]"
      />
      {snackbarProps.show && (
        <Snackbar
          states="error"
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText="Dismiss"
          message={snackbarProps.message}
          onTextClick={() => {
            setSnackbarProps(props => ({
              ...props,
              show: false,
            }));
          }}
          className={'fixed center-horizontal top-[60px]'}
        />
      )}
      {generateClass2SmartWorksheetMutation.isLoading && (
        <LoadingFull disableDecorations={Boolean(isJC)}>
          <div
            className={`text-center ${
              isJC ? 'flex flex-col items-center' : ''
            }`}
          >
            {Boolean(isJC) && (
              <img
                src={`${CDN_URL}assets/animations/GB-loading-minimalistic.gif`}
                className="mb-5"
                alt="loading spinner"
                width={25}
                height={25}
              />
            )}
            <p className={'mb-5 font-black text-xl leading-6 text-white'}>
              Creating Worksheet…
            </p>
            <p className={'mb-0 text-white'}>
              Take your time to learn and understand the concepts, you'll get
              there
            </p>
          </div>
        </LoadingFull>
      )}
    </>
  );
};

export default Class2SmartPopupHandler;
